import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import { ProvidentFundService } from '../../services/providentFund.service';
import { IPfContributionRow, IPfRun, IPfRunDetail, IPfRunPreview, IPfSkippedRow } from '../../interfaces/providentFund.interface';
import { useToast } from '../../context/ToastContext';
import {
    ConfirmDialog, JournalLink, PeriodPicker, PreviewNotices, RunStatusChip, SummaryStats, currentPeriod, money,
    periodLabel, useSegmentNames,
} from './provisionShared';

interface Props {
    institutionId: string;
    canManage: boolean;
}

// W3-J: monthly provident-fund contribution runs (draft preview, post, reverse).
const PfRunsTab: React.FC<Props> = ({ institutionId, canManage }) => {
    const { showSuccess, showApiError } = useToast();
    const segmentName = useSegmentNames(institutionId);
    const [period, setPeriod] = useState(currentPeriod);
    const [preview, setPreview] = useState<IPfRunPreview | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [posting, setPosting] = useState(false);
    const [runs, setRuns] = useState<IPfRun[]>([]);
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState<IPfRunDetail | null>(null);
    const [reverseTarget, setReverseTarget] = useState<IPfRun | null>(null);
    const [reversing, setReversing] = useState(false);

    const loadRuns = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            setRuns(await ProvidentFundService.listRuns(institutionId));
        } catch (e) {
            showApiError(e, 'Failed to load contribution runs');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => { loadRuns(); }, [loadRuns]);

    const runPreview = async () => {
        setPreviewing(true);
        try {
            setPreview(await ProvidentFundService.previewRun(institutionId, period.year, period.month));
        } catch (e) {
            setPreview(null);
            showApiError(e, 'Failed to preview the contribution run');
        } finally {
            setPreviewing(false);
        }
    };

    const post = async () => {
        setPosting(true);
        try {
            await ProvidentFundService.postRun(institutionId, period);
            showSuccess(`Provident fund run for ${periodLabel(period.year, period.month)} posted`);
            setPreview(null);
            await loadRuns();
        } catch (e) {
            showApiError(e, 'Failed to post the contribution run');
        } finally {
            setPosting(false);
        }
    };

    const reverse = async () => {
        if (!reverseTarget) return;
        setReversing(true);
        try {
            await ProvidentFundService.reverseRun(reverseTarget.id);
            showSuccess('Contribution run reversed');
            setReverseTarget(null);
            await loadRuns();
        } catch (e) {
            showApiError(e, 'Failed to reverse the run');
        } finally {
            setReversing(false);
        }
    };

    const openDetail = async (id: string) => {
        try {
            setDetail(await ProvidentFundService.getRun(id));
        } catch (e) {
            showApiError(e, 'Failed to load the run');
        }
    };

    const rowColumns: Column<IPfContributionRow>[] = [
        { key: 'emp', header: 'Employee', priority: 1, helpKey: 'payroll.pfRuns.row.col.emp', render: r => `${r.employeeName}${r.employeeCode ? ` (${r.employeeCode})` : ''}` },
        { key: 'seg', header: 'Segment', priority: 3, helpKey: 'payroll.pfRuns.row.col.seg', render: r => segmentName(r.segmentId) },
        { key: 'ee', header: 'Employee share', priority: 1, align: 'right', helpKey: 'payroll.pfRuns.row.col.ee', render: r => money(r.employeeShare) },
        { key: 'er', header: 'Employer share', priority: 1, align: 'right', helpKey: 'payroll.pfRuns.row.col.er', render: r => money(r.employerShare) },
        { key: 'acc', header: 'Accrued', priority: 3, helpKey: 'payroll.pfRuns.row.col.acc', render: r => (r.accrued ? 'Yes' : 'No') },
    ];

    const skippedColumns: Column<IPfSkippedRow>[] = [
        { key: 'emp', header: 'Employee', priority: 1, helpKey: 'payroll.pfRuns.skipped.col.emp', render: r => r.employeeName },
        { key: 'why', header: 'Reason', priority: 1, helpKey: 'payroll.pfRuns.skipped.col.why', render: r => r.reason },
    ];

    const runColumns: Column<IPfRun>[] = [
        { key: 'period', header: 'Period', priority: 1, helpKey: 'payroll.pfRuns.run.col.period', render: r => periodLabel(r.year, r.month) },
        { key: 'status', header: 'Status', priority: 1, helpKey: 'payroll.pfRuns.run.col.status', render: r => <RunStatusChip status={r.status} /> },
        { key: 'mem', header: 'Members', priority: 3, align: 'right', helpKey: 'payroll.pfRuns.run.col.mem', render: r => r.memberCount },
        { key: 'ee', header: 'Employee total', priority: 2, align: 'right', helpKey: 'payroll.pfRuns.run.col.ee', render: r => money(r.employeeTotal) },
        { key: 'er', header: 'Employer total', priority: 2, align: 'right', helpKey: 'payroll.pfRuns.run.col.er', render: r => money(r.employerTotal) },
        { key: 'rc', header: 'Reclass', priority: 3, align: 'right', helpKey: 'payroll.pfRuns.run.col.rc', render: r => money(r.reclassTotal) },
        { key: 'je', header: 'Journal', priority: 3, helpKey: 'payroll.pfRuns.run.col.je', render: r => <JournalLink id={r.journalEntryId} /> },
        {
            key: 'actions', header: 'Actions', helpKey: 'payroll.pfRuns.run.col.actions', role: 'actions', hideInCard: true,
            render: r => (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" data-help="payroll.pfRuns.run.action.details" onClick={() => openDetail(r.id)}>Details</Button>
                    {canManage && r.status === 'POSTED' && (
                        <Button size="small" color="error" data-help="payroll.pfRuns.run.action.reverse" onClick={() => setReverseTarget(r)}>Reverse</Button>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box data-help-page="payroll.pfRuns.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="payroll.pfRuns.toolbar">
                <PeriodPicker year={period.year} month={period.month} onChange={p => { setPeriod(p); setPreview(null); }} />
                <Button data-help="payroll.pfRuns.preview" variant="outlined" onClick={runPreview} disabled={!institutionId || previewing}>Preview</Button>
                {canManage && (
                    <Button data-help="payroll.pfRuns.post" variant="contained" onClick={post} disabled={!preview || !!preview.blocker || posting}>Post run</Button>
                )}
            </Box>

            {previewing && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
            {preview && !previewing && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Draft for {periodLabel(preview.year, preview.month)}</Typography>
                    <PreviewNotices blocker={preview.blocker} warnings={preview.warnings ?? []} />
                    <SummaryStats items={[
                        { label: 'Employee total', value: money(preview.employeeTotal) },
                        { label: 'Employer total', value: money(preview.employerTotal) },
                        { label: 'Reclass total', value: money(preview.reclassTotal) },
                        { label: 'Legacy rows', value: String(preview.legacyRows) },
                    ]} />
                    <ResponsiveTable data-help-section="payroll.pfRuns.previewTable" pageKey="pf-run-preview" columns={rowColumns} rows={preview.rows}
                        getRowKey={r => r.userId} emptyMessage="No contributions for this period." />
                    {preview.skipped.length > 0 && (
                        <Box sx={{ mt: 2 }} data-help-section="payroll.pfRuns.skippedTable">
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Skipped ({preview.skipped.length})</Typography>
                            <ResponsiveTable pageKey="pf-run-skipped" columns={skippedColumns} rows={preview.skipped}
                                getRowKey={r => `${r.userId}-${r.reason}`} emptyMessage="" />
                        </Box>
                    )}
                </Box>
            )}

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Runs</Typography>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : (
                <ResponsiveTable data-help-section="payroll.pfRuns.runsTable" pageKey="pf-runs" columns={runColumns} rows={runs} getRowKey={r => r.id}
                    emptyMessage="No contribution runs yet." />
            )}

            <ConfirmDialog
                open={!!reverseTarget} title="Reverse contribution run" confirmLabel="Reverse" busy={reversing}
                message={reverseTarget && `Reverse the provident fund run for ${periodLabel(reverseTarget.year, reverseTarget.month)}? The ledger entries and journal are reversed.`}
                onConfirm={reverse} onClose={() => setReverseTarget(null)}
            />

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
                <DialogTitle>{detail && `Contribution run, ${periodLabel(detail.run.year, detail.run.month)}`}</DialogTitle>
                <DialogContent dividers>
                    {detail && (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                                <RunStatusChip status={detail.run.status} />
                                <JournalLink id={detail.run.journalEntryId} />
                                {detail.run.reversalJournalEntryId && <JournalLink id={detail.run.reversalJournalEntryId} label="View reversal JE" />}
                            </Box>
                            <ResponsiveTable data-help-section="payroll.pfRuns.detailTable" pageKey="pf-run-detail" columns={rowColumns} rows={detail.rows}
                                getRowKey={r => r.userId} emptyMessage="No rows." />
                        </>
                    )}
                </DialogContent>
                <DialogActions><Button data-help="payroll.pfRuns.detailClose" onClick={() => setDetail(null)}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export default PfRunsTab;
